import React, { useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules';
import { Box, Typography, Card, CardMedia, CardContent, CircularProgress, Alert } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import promotionalService from '../../services/promotionalService';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';

const HeroAdCarousal = () => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAds = async () => {
      try {
        setLoading(true);
        const response = await promotionalService.getAdCarousels({
          is_active: true,
          page: 1
        });
        
        setAds(response.data.results || []);
      } catch (err) {
        console.error('Error fetching ads:', err);
        setError('Failed to load advertisements');
      } finally {
        setLoading(false);
      }
    };

    fetchAds();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mb: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (ads.length === 0) {
    return null; // Don't render anything if no ads
  }

  return (
    <Box sx={{ mb: 4, position: 'relative' }}>
      <Swiper
        modules={[Navigation, Pagination, Autoplay, EffectFade]}
        spaceBetween={0}
        slidesPerView={1}
        navigation={{
          nextEl: '.swiper-button-next-custom',
          prevEl: '.swiper-button-prev-custom',
        }}
        pagination={{
          clickable: true,
          bulletClass: 'swiper-pagination-bullet-custom',
          bulletActiveClass: 'swiper-pagination-bullet-active-custom',
        }}
        autoplay={{
          delay: 5000,
          disableOnInteraction: false,
        }}
        effect="fade"
        fadeEffect={{
          crossFade: true,
        }}
        loop={ads.length > 1}
        style={{
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        }}
      >
        {ads.map((ad) => (
          <SwiperSlide key={ad.id}>
            <Card
              sx={{
                position: 'relative',
                height: { xs: '250px', sm: '300px', md: '400px' },
                backgroundColor: 'white',
                cursor: ad.link_url ? 'pointer' : 'default',
                '&:hover': ad.link_url ? {
                  transform: 'scale(1.02)',
                  transition: 'transform 0.3s ease-in-out',
                } : {},
              }}
              onClick={() => {
                if (ad.link_url) {
                  window.open(ad.link_url, '_blank');
                }
              }}
            >
              <CardMedia
                component="img"
                height="100%"
                image={ad.image_url}
                alt={ad.title || 'Advertisement'}
                sx={{
                  objectFit: 'cover',
                  width: '100%',
                }}
              />
            </Card>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Custom Navigation Buttons */}
      {ads.length > 1 && (
        <>
          <Box
            className="swiper-button-prev-custom"
            sx={{
              position: 'absolute',
              left: { xs: 8, sm: 12, md: 16 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '50%',
              width: { xs: 32, sm: 40, md: 48 },
              height: { xs: 32, sm: 40, md: 48 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
                transform: 'translateY(-50%) scale(1.1)',
              },
            }}
          >
            <ChevronLeft sx={{ color: 'primary.main', fontSize: { xs: 16, sm: 20, md: 24, lg: 28 } }} />
          </Box>
          <Box
            className="swiper-button-next-custom"
            sx={{
              position: 'absolute',
              right: { xs: 8, sm: 12, md: 16 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '50%',
              width: { xs: 32, sm: 40, md: 48 },
              height: { xs: 32, sm: 40, md: 48 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
                transform: 'translateY(-50%) scale(1.1)',
              },
            }}
          >
            <ChevronRight sx={{ color: 'primary.main', fontSize: { xs: 16, sm: 20, md: 24, lg: 28 } }} />
          </Box>
        </>
      )}

      {/* Custom Styles */}
      <style jsx global>{`
        .swiper-pagination {
          bottom: 20px !important;
        }
        .swiper-pagination-bullet-custom {
          background: rgba(255, 255, 255, 0.5) !important;
          opacity: 1 !important;
          width: 12px !important;
          height: 12px !important;
          margin: 0 6px !important;
          transition: all 0.3s ease !important;
        }
        .swiper-pagination-bullet-active-custom {
          background: #1976d2 !important;
          transform: scale(1.2) !important;
        }
        .swiper-button-disabled {
          opacity: 0.3 !important;
          cursor: not-allowed !important;
        }
      `}</style>
    </Box>
  );
};

export default HeroAdCarousal;
